/* =========================================================================
   Geolocatie.js — VERSIUNE ÎNTĂRITĂ PENTRU TEMA DAWN
   - restricții geolocație pentru SAN MARCO
   - B2B bypass
   - GPS -> IP -> reverse geocoding
   - cache sessionStorage
   - fail-open configurabil
   - blocare click / submit / fetch / XHR pentru /cart/add
   - Roomvo controlat separat prin triggerul roomvo.alwaysVisible
   ======================================================================= */

const GEOLOCATION_CONFIG = {
  useHTML5Geolocation: true,

  // Pentru testare: pune true ca să simulezi București și să fie BLOCAT
  simulateLocation: false,
  simulatedCoords: { lat: 44.4268, lng: 26.1025 },

  clearCacheOnSimulate: true,
  fallbackUrl: "https://www.sanmag.ro",

  geolocationTimeout: 6000,
  preferHTML5Ms: 2000, // păstrat pentru compatibilitate
  reverseGeocodingTimeout: 5000,
  ipLookupTimeout: 4000,

  cacheTtlMinutes: 15,
  failOpenOnError: false,
  debug: true,

  vendorDetection: {
    waitMs: 1200,
    observerStopMs: 12000
  },

  sessionStorageKeys: {
    cartAccess: "userCanAddToCart",
    roomvoAccess: "allowRoomvoLaunch",
    coords: "geo_cached_coords",
    coordsTs: "geo_cached_ts",
    metrics: "geo_metrics"
  },

  requiredZone: {
    name: "Iași",
    lat: 47.151726,
    lng: 27.587914,
    radius: 40,
    vendor: "SAN MARCO",
    cityVariants: ["iași", "iasi", "iaşi", "jassy"]
  },

  vendorSelectors: [
    ".product__text.inline-richtext.caption-with-letter-spacing",
    ".product__vendor",
    "meta[name='product:vendor']"
  ],

  restrictionSelectors: [
    ".price",
    ".product-form__buttons",
    ".product-form__input.product-form__quantity",
    ".shopify-payment-button",
    "button[name='add']",
    ".price-item",
    ".product-form__submit"
  ],

  ipProviders: [
    {
      url: "https://ipapi.co/json/",
      parser: (data) =>
        data?.latitude && data?.longitude
          ? { lat: Number(data.latitude), lng: Number(data.longitude), city: data.city || "" }
          : null,
      source: "ipapi"
    },
    {
      url: "https://ipwho.is/",
      parser: (data) =>
        data?.latitude && data?.longitude
          ? { lat: Number(data.latitude), lng: Number(data.longitude), city: data.city || "" }
          : null,
      source: "ipwho"
    }
  ],

  reverseGeocodingApis: [
    {
      url: (lat, lng) =>
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=ro`,
      parser: (data) => data?.city || data?.locality || "",
      source: "bigdatacloud"
    },
    {
      url: (lat, lng) =>
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ro`,
      parser: (data) => {
        const addr = data?.address || {};
        return addr.city || addr.town || addr.village || addr.municipality || "";
      },
      source: "openstreetmap"
    }
  ],

  roomvo: {
    alwaysVisible: true, // true = vizibil și funcțional pentru toți; false = urmează restricțiile
    maxRetries: 5,
    retryDelayMs: 800,
    checkIntervalMs: 300,
    selectors: [
      ".roomvo-button",
      ".roomvo-launch",
      ".roomvo-trigger",
      "[data-roomvo]",
      "[data-roomvo-launch]",
      "[class*='roomvo']",
      "[id*='roomvo']"
    ]
  }
};

const GEOlog = (...args) => {
  if (!GEOLOCATION_CONFIG.debug) return;
  const t = new Date().toISOString().split("T")[1].split(".")[0];
  console.log(`[GEO ${t}]`, ...args);
};

class FreeGeoLocationManager {
  constructor() {
    this.hasRun = false;
    this.interceptorsAttached = false;
    this.fetchPatched = false;
    this.xhrPatched = false;
    this.preLockObserver = null;
    this.preLockStopTimer = null;
    this.hasRedirected = false;

    this.metrics = {
      startTime: Date.now(),
      gpsAttempted: false,
      gpsSuccess: false,
      ipSuccess: false,
      reverseGeocodingAttempts: [],
      source: null,
      accuracy: null,
      detectedCity: null,
      totalTime: 0,
      decision: null,
      errors: []
    };

    this.setupAccessDefaults();

    if (GEOLOCATION_CONFIG.simulateLocation && GEOLOCATION_CONFIG.clearCacheOnSimulate) {
      this.forceClearCache();
    }

    this.attachInterceptors();
    this.preCheckAndLock();
    this.init();
  }

  init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.run(), { once: true });
    } else {
      this.run();
    }
  }

  async run() {
    if (this.hasRun) return;
    this.hasRun = true;

    try {
      if (window.GEO_USER?.isB2B) {
        GEOlog("B2B detectat -> acces permis");
        this.allowAccess();
        this.finalize({ allowed: true, reason: "B2B bypass", metric: "b2b_bypass" });
        return;
      }

      const restrictedVendor = await this.ensureVendorResolved();

      if (!restrictedVendor) {
        GEOlog("Vendorul nu este SAN MARCO -> fără restricții");
        this.allowAccess();
        this.finalize({ allowed: true, reason: "Vendor nerestricționat", metric: "vendor_not_restricted" });
        return;
      }

      const coords = await this.resolveLocation();

      if (!coords) {
        GEOlog("Coordonate indisponibile");
        if (GEOLOCATION_CONFIG.failOpenOnError) {
          this.allowAccess();
          this.finalize({ allowed: true, reason: "Fail-open la eroare geolocație", metric: "fail_open_no_coords" });
        } else {
          this.denyAccess();
          this.finalize({ allowed: false, reason: "Fără coordonate", metric: "fail_closed_no_coords" });
        }
        return;
      }

      this.metrics.accuracy = coords.accuracy ?? null;

      const detectedCity = await this.confirmCityWithFallback(coords.lat, coords.lng);
      this.metrics.detectedCity = detectedCity || "";

      const decision = this.makeAccessDecision(coords, detectedCity);

      if (decision.allowed) {
        this.allowAccess();
      } else {
        this.denyAccess();
      }

      this.finalize(decision);
    } catch (error) {
      this.recordError("run", error);
      GEOlog("Eroare generală în run():", error);

      if (GEOLOCATION_CONFIG.failOpenOnError) {
        this.allowAccess();
        this.finalize({ allowed: true, reason: "Fail-open la excepție", metric: "fail_open_exception" });
      } else {
        this.denyAccess();
        this.finalize({ allowed: false, reason: "Excepție în run()", metric: "fail_closed_exception" });
      }
    }
  }

  finalize(decision) {
    this.metrics.totalTime = Date.now() - this.metrics.startTime;
    this.metrics.decision = decision;
    this.saveMetrics();
    this.stopPreLockObserver();

    GEOlog(`Final: ${decision.allowed ? "PERMIS" : "REFUZAT"} | ${decision.reason} | ${this.metrics.totalTime}ms`);
  }

  setupAccessDefaults() {
    window.userCanAddToCart = false;
    window.allowRoomvoLaunch = this.shouldKeepRoomvoVisible();

    document.body?.classList.remove("geo-access-granted");

    this.safeSessionSet(GEOLOCATION_CONFIG.sessionStorageKeys.cartAccess, "false");
    this.safeSessionSet(
      GEOLOCATION_CONFIG.sessionStorageKeys.roomvoAccess,
      this.shouldKeepRoomvoVisible() ? "true" : "false"
    );
  }

  shouldKeepRoomvoVisible() {
    return GEOLOCATION_CONFIG.roomvo?.alwaysVisible === true;
  }

  getRestrictionSelectors() {
    const selectors = [...GEOLOCATION_CONFIG.restrictionSelectors];

    if (!this.shouldKeepRoomvoVisible()) {
      selectors.push(...(GEOLOCATION_CONFIG.roomvo?.selectors || []));
    }

    return selectors;
  }

  forceClearCache() {
    try {
      const keys = Object.values(GEOLOCATION_CONFIG.sessionStorageKeys);
      keys.forEach((key) => sessionStorage.removeItem(key));
      GEOlog("🗑️ Cache șters (mod test)");
    } catch (error) {
      this.recordError("forceClearCache", error);
    }
  }

  preCheckAndLock() {
    const lockIfRequired = () => {
      if (this.isVendorRestricted()) {
        this.applyRestrictions();
        return true;
      }
      return false;
    };

    if (lockIfRequired()) return;

    if (!window.MutationObserver || !document.documentElement) return;

    this.preLockObserver = new MutationObserver(() => {
      if (lockIfRequired()) {
        GEOlog("Vendor detectat prin observer -> restricții aplicate preventiv");
        this.stopPreLockObserver();
      }
    });

    this.preLockObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: false
    });

    this.preLockStopTimer = window.setTimeout(() => {
      this.stopPreLockObserver();
    }, GEOLOCATION_CONFIG.vendorDetection.observerStopMs);
  }

  stopPreLockObserver() {
    if (this.preLockObserver) {
      this.preLockObserver.disconnect();
      this.preLockObserver = null;
    }

    if (this.preLockStopTimer) {
      clearTimeout(this.preLockStopTimer);
      this.preLockStopTimer = null;
    }
  }

  async ensureVendorResolved() {
    if (this.isVendorRestricted()) return true;

    if (!this.isLikelyProductPage()) return false;

    return this.waitForRestrictedVendor(GEOLOCATION_CONFIG.vendorDetection.waitMs);
  }

  isLikelyProductPage() {
    return Boolean(
      document.querySelector(
        "form[action*='/cart/add'], .product, .product__info-wrapper, [data-product-id], [id*='MainProduct']"
      )
    );
  }

  waitForRestrictedVendor(maxWaitMs) {
    return new Promise((resolve) => {
      if (this.isVendorRestricted()) {
        resolve(true);
        return;
      }

      if (!window.MutationObserver || !document.documentElement) {
        window.setTimeout(() => resolve(this.isVendorRestricted()), maxWaitMs);
        return;
      }

      let resolved = false;

      const finish = (value) => {
        if (resolved) return;
        resolved = true;
        observer.disconnect();
        clearTimeout(timer);
        resolve(value);
      };

      const observer = new MutationObserver(() => {
        if (this.isVendorRestricted()) finish(true);
      });

      const timer = window.setTimeout(() => finish(this.isVendorRestricted()), maxWaitMs);

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    });
  }

  isVendorRestricted() {
    for (const sel of GEOLOCATION_CONFIG.vendorSelectors) {
      const el = document.querySelector(sel);
      if (!el) continue;

      const value = el.tagName === "META" ? el.getAttribute("content") : el.textContent;

      if (
        value &&
        value.trim().toUpperCase().includes(GEOLOCATION_CONFIG.requiredZone.vendor.toUpperCase())
      ) {
        return true;
      }
    }

    return false;
  }

  normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  isRoomvoTrigger(target) {
    if (!(target instanceof Element)) return false;

    const el = target.closest("a, button, div, span");
    if (!el) return false;

    const text = this.normalizeText(el.textContent);
    const cls = this.normalizeText(el.className);
    const id = this.normalizeText(el.id);

    if (
      text.includes("vezi in camera ta") ||
      text.includes("vezi in camera") ||
      text.includes("view in your room") ||
      text.includes("roomvo")
    ) {
      return true;
    }

    if (
      cls.includes("roomvo") ||
      id.includes("roomvo") ||
      el.hasAttribute("data-roomvo") ||
      el.hasAttribute("data-roomvo-launch")
    ) {
      return true;
    }

    return false;
  }

  hideRoomvoButtons() {
    if (this.shouldKeepRoomvoVisible()) return;
    if (window.userCanAddToCart === true) return;
    if (!this.isVendorRestricted()) return;

    const roomvoSelectorList = [
      ...(GEOLOCATION_CONFIG.roomvo?.selectors || []),
      "a",
      "button",
      "div",
      "span"
    ];

    const candidates = document.querySelectorAll(roomvoSelectorList.join(","));

    candidates.forEach((el) => {
      if (this.isRoomvoTrigger(el)) {
        el.style.setProperty("display", "none", "important");
        el.style.setProperty("visibility", "hidden", "important");
        el.style.setProperty("pointer-events", "none", "important");
      }
    });
  }

  applyRestrictions() {
    if (this.isCrawler()) return;

    const existing = document.getElementById("geolocation-restrictions");
    if (existing) existing.remove();

    const selectors = this.getRestrictionSelectors();
    if (!selectors.length) return;

    const style = document.createElement("style");
    style.id = "geolocation-restrictions";
    style.textContent = `
      ${selectors.join(",\n")} {
        display: none !important;
        visibility: hidden !important;
      }
    `;

    (document.head || document.documentElement).appendChild(style);

    if (!this.shouldKeepRoomvoVisible()) {
      this.hideRoomvoButtons();
    }

    GEOlog("🚫 Restricții aplicate preventiv");
  }

  allowAccess() {
    window.userCanAddToCart = true;
    window.allowRoomvoLaunch = true;

    document.body?.classList.add("geo-access-granted");
    document.getElementById("geolocation-restrictions")?.remove();

    this.safeSessionSet(GEOLOCATION_CONFIG.sessionStorageKeys.cartAccess, "true");
    this.safeSessionSet(GEOLOCATION_CONFIG.sessionStorageKeys.roomvoAccess, "true");

    this.reinitRoomvoWithRetry();
    GEOlog("✅ Acces PERMIS");
  }

  denyAccess() {
    window.userCanAddToCart = false;
    window.allowRoomvoLaunch = this.shouldKeepRoomvoVisible();

    document.body?.classList.remove("geo-access-granted");
    this.applyRestrictions();

    this.safeSessionSet(GEOLOCATION_CONFIG.sessionStorageKeys.cartAccess, "false");
    this.safeSessionSet(
      GEOLOCATION_CONFIG.sessionStorageKeys.roomvoAccess,
      this.shouldKeepRoomvoVisible() ? "true" : "false"
    );

    if (!this.shouldKeepRoomvoVisible()) {
      this.hideRoomvoButtons();
    }

    GEOlog("🚫 Acces REFUZAT");
  }

  async resolveLocation() {
    if (GEOLOCATION_CONFIG.simulateLocation) {
      this.metrics.source = "simulate";
      return {
        ...GEOLOCATION_CONFIG.simulatedCoords,
        accuracy: 10
      };
    }

    const cached = this.getCachedCoords();
    if (cached) {
      this.metrics.source = "cache";
      return cached;
    }

    let gpsPromise = null;

    if (GEOLOCATION_CONFIG.useHTML5Geolocation && navigator.geolocation) {
      this.metrics.gpsAttempted = true;
      gpsPromise = this.getHTML5Coords().catch((error) => {
        this.recordError("html5_geolocation", error);
        return null;
      });
    }

    if (gpsPromise) {
      const gps = await gpsPromise;
      if (gps) {
        this.setCachedCoords(gps);
        return gps;
      }
    }

    const ip = await this.getFastestIPCoords().catch((error) => {
      this.recordError("ip_lookup", error);
      return null;
    });

    if (ip) {
      this.setCachedCoords(ip);
      return ip;
    }

    return null;
  }

  getHTML5Coords() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.metrics.gpsSuccess = true;
          this.metrics.source = "html5";

          resolve({
            lat: Number(pos.coords.latitude),
            lng: Number(pos.coords.longitude),
            accuracy: Number(pos.coords.accuracy)
          });
        },
        (err) => reject(err),
        {
          timeout: GEOLOCATION_CONFIG.geolocationTimeout,
          enableHighAccuracy: true,
          maximumAge: 0
        }
      );
    });
  }

  async getFastestIPCoords() {
    const providers = GEOLOCATION_CONFIG.ipProviders.map((provider) => async () => {
      const data = await this.fetchJsonWithTimeout(provider.url, GEOLOCATION_CONFIG.ipLookupTimeout);
      const coords = provider.parser(data);

      if (!coords) {
        throw new Error(`Provider ${provider.source} nu a returnat coordonate valide`);
      }

      this.metrics.ipSuccess = true;
      this.metrics.source = `ip:${provider.source}`;
      return coords;
    });

    return this.firstSuccessful(providers);
  }

  async confirmCityWithFallback(lat, lng) {
    for (const api of GEOLOCATION_CONFIG.reverseGeocodingApis) {
      try {
        const data = await this.fetchJsonWithTimeout(
          api.url(lat, lng),
          GEOLOCATION_CONFIG.reverseGeocodingTimeout
        );

        const city = (api.parser(data) || "").trim();

        this.metrics.reverseGeocodingAttempts.push({
          source: api.source,
          success: Boolean(city),
          city
        });

        if (city) return city;
      } catch (error) {
        this.metrics.reverseGeocodingAttempts.push({
          source: api.source,
          success: false,
          city: ""
        });
        this.recordError(`reverse_geocoding:${api.source}`, error);
      }
    }

    return "";
  }

  makeAccessDecision(coords, detectedCity) {
    const zone = GEOLOCATION_CONFIG.requiredZone;
    const distanceKm = this.calculateDistance(coords.lat, coords.lng, zone.lat, zone.lng);
    const cityMatch = this.isCityInIasi(detectedCity);

    GEOlog(`Analiză: dist=${distanceKm.toFixed(2)}km, city="${detectedCity || "-"}", match=${cityMatch}`);

    if (cityMatch) {
      return {
        allowed: true,
        reason: "Oraș confirmat",
        metric: "city_override"
      };
    }

    if (distanceKm <= zone.radius) {
      return {
        allowed: true,
        reason: "În rază",
        metric: "in_zone"
      };
    }

    return {
      allowed: false,
      reason: "În afara razei",
      metric: "out_of_zone"
    };
  }

  isCityInIasi(cityName) {
    if (!cityName) return false;

    const normalize = (s) =>
      String(s)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z]/g, "");

    const cityNorm = normalize(cityName);

    return GEOLOCATION_CONFIG.requiredZone.cityVariants.some((variant) =>
      cityNorm.includes(normalize(variant))
    );
  }

  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }

  attachInterceptors() {
    if (this.interceptorsAttached) return;
    this.interceptorsAttached = true;

    document.addEventListener(
      "click",
      (e) => {
        if (!this.shouldEnforceRestriction()) return;

        const isBlockedAddToCart = this.isAddToCartClick(e.target);
        const isBlockedRoomvo = !this.shouldKeepRoomvoVisible() && this.isRoomvoTrigger(e.target);

        if (!isBlockedAddToCart && !isBlockedRoomvo) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();

        GEOlog(isBlockedRoomvo ? "Blocat click Roomvo" : "Blocat click add-to-cart");
        this.redirectToFallback();
      },
      true
    );

    document.addEventListener(
      "submit",
      (e) => {
        if (!this.shouldEnforceRestriction()) return;

        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!this.isAddToCartForm(form)) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();

        GEOlog("Blocat submit form /cart/add");
        this.redirectToFallback();
      },
      true
    );

    this.patchFetch();
    this.patchXHR();
  }

  shouldEnforceRestriction() {
    if (window.GEO_USER?.isB2B) return false;
    if (window.userCanAddToCart === true) return false;
    return this.isVendorRestricted();
  }

  isAddToCartClick(target) {
    if (!(target instanceof Element)) return false;

    const directMatch = target.closest(
      "a[href*='/cart/add'], button[name='add'], input[name='add'], .product-form__submit"
    );
    if (directMatch) return true;

    const submitLike = target.closest("button[type='submit'], input[type='submit']");
    if (submitLike && submitLike.closest("form[action*='/cart/add']")) return true;

    return false;
  }

  isAddToCartForm(form) {
    return form.matches("form[action*='/cart/add']");
  }

  patchFetch() {
    if (this.fetchPatched || typeof window.fetch !== "function") return;
    this.fetchPatched = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
      const url = this.extractRequestUrl(input);
      const method =
        String(
          init.method ||
            (input && typeof input === "object" && "method" in input ? input.method : "GET")
        ).toUpperCase();

      if (this.shouldBlockCartRequest(url, method)) {
        GEOlog("Blocat fetch către /cart/add:", method, url);
        this.redirectToFallback();
        return Promise.reject(new Error("Geolocation restriction: cart/add blocked"));
      }

      return originalFetch(input, init);
    };
  }

  patchXHR() {
    if (this.xhrPatched || typeof XMLHttpRequest === "undefined") return;
    this.xhrPatched = true;

    const manager = this;
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__geoMethod = String(method || "GET").toUpperCase();
      this.__geoUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (manager.shouldBlockCartRequest(this.__geoUrl, this.__geoMethod)) {
        GEOlog("Blocat XHR către /cart/add:", this.__geoMethod, this.__geoUrl);
        manager.redirectToFallback();

        try {
          this.abort();
        } catch (_) {}

        if (typeof this.onerror === "function") {
          setTimeout(() => this.onerror(new Error("Geolocation restriction: cart/add blocked")), 0);
        }

        return;
      }

      return originalSend.apply(this, args);
    };
  }

  extractRequestUrl(input) {
    if (!input) return "";

    if (typeof input === "string") return input;

    if (typeof Request !== "undefined" && input instanceof Request) {
      return input.url || "";
    }

    if (typeof input === "object" && "url" in input) {
      return input.url || "";
    }

    return "";
  }

  shouldBlockCartRequest(rawUrl, method = "GET") {
    if (!this.shouldEnforceRestriction()) return false;
    if (!rawUrl) return false;

    try {
      const url = new URL(rawUrl, window.location.origin);
      const path = url.pathname.toLowerCase();

      if (!path.includes("/cart/add")) return false;

      return ["GET", "POST"].includes(String(method).toUpperCase());
    } catch (_) {
      const normalized = String(rawUrl).toLowerCase();
      return normalized.includes("/cart/add");
    }
  }

  redirectToFallback() {
    if (this.hasRedirected) return;
    this.hasRedirected = true;

    window.setTimeout(() => {
      window.location.href = GEOLOCATION_CONFIG.fallbackUrl;
    }, 0);
  }

  async fetchJsonWithTimeout(url, timeoutMs) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} pentru ${url}`);
      }

      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  async firstSuccessful(taskFactories) {
    return new Promise((resolve, reject) => {
      if (!Array.isArray(taskFactories) || taskFactories.length === 0) {
        reject(new Error("Nu există taskuri"));
        return;
      }

      let settled = false;
      let failures = 0;
      const errors = [];

      taskFactories.forEach(async (factory, index) => {
        try {
          const result = await factory();
          if (settled) return;

          if (result !== null && result !== undefined) {
            settled = true;
            resolve(result);
            return;
          }

          throw new Error("Rezultat gol");
        } catch (error) {
          errors[index] = error;
          failures += 1;

          if (!settled && failures === taskFactories.length) {
            reject(new Error(errors.map((e) => e?.message || "error").join(" | ")));
          }
        }
      });
    });
  }

  setCachedCoords(coords) {
    try {
      this.safeSessionSet(GEOLOCATION_CONFIG.sessionStorageKeys.coords, JSON.stringify(coords));
      this.safeSessionSet(GEOLOCATION_CONFIG.sessionStorageKeys.coordsTs, String(Date.now()));
    } catch (error) {
      this.recordError("setCachedCoords", error);
    }
  }

  getCachedCoords() {
    try {
      const raw = this.safeSessionGet(GEOLOCATION_CONFIG.sessionStorageKeys.coords);
      const tsRaw = this.safeSessionGet(GEOLOCATION_CONFIG.sessionStorageKeys.coordsTs);

      if (!raw || !tsRaw) return null;

      const ts = Number(tsRaw);
      if (!Number.isFinite(ts)) return null;

      const ageMinutes = (Date.now() - ts) / 60000;
      if (ageMinutes > GEOLOCATION_CONFIG.cacheTtlMinutes) return null;

      const parsed = JSON.parse(raw);

      if (!parsed || typeof parsed.lat !== "number" || typeof parsed.lng !== "number") {
        return null;
      }

      return parsed;
    } catch (error) {
      this.recordError("getCachedCoords", error);
      return null;
    }
  }

  saveMetrics() {
    try {
      this.safeSessionSet(
        GEOLOCATION_CONFIG.sessionStorageKeys.metrics,
        JSON.stringify(this.metrics)
      );
    } catch (error) {
      this.recordError("saveMetrics", error);
    }
  }

  reinitRoomvoWithRetry() {
    let attempts = 0;

    const interval = window.setInterval(() => {
      attempts += 1;

      try {
        if (window.roomvo?.refresh) {
          window.roomvo.refresh();
          GEOlog("Roomvo refresh executat");
          clearInterval(interval);
          return;
        }
      } catch (error) {
        this.recordError("roomvo_refresh", error);
      }

      if (attempts >= GEOLOCATION_CONFIG.roomvo.maxRetries) {
        clearInterval(interval);
      }
    }, GEOLOCATION_CONFIG.roomvo.retryDelayMs);
  }

  isCrawler() {
    const ua = String(navigator.userAgent || "").toLowerCase();
    return ["googlebot", "bingbot", "baiduspider", "facebookexternalhit"].some((bot) =>
      ua.includes(bot)
    );
  }

  safeSessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (_) {}
  }

  safeSessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  recordError(stage, error) {
    const message =
      error?.message ||
      error?.code ||
      (typeof error === "string" ? error : "unknown_error");

    this.metrics.errors.push({
      stage,
      message: String(message)
    });
  }
}

new FreeGeoLocationManager();